import API from "Classes/Api";
import {
  ApiDeleteCallParams,
  ApiGetCallParams,
  ApiPostCallParams,
  ApiPutCallParams,
} from "Classes/ApiCallParams";
import {
  AssignAlertTypeModel,
  AssignResourceModel,
  AssignNotificationChannelsModel,
  CreateProfileModel,
  UpdateProfileDescriptionModel,
} from "../../../../pages/AdminConsole/Monitoring/Profiles/profileTypes";
import { ALL_OBJECTS_LABEL } from "pages/AdminConsole/Monitoring/Profiles/profileConsts";

interface GetProfileParams {
  clientId: number;
  profileId: number;
  view?: "Basic" | "Full" | "AlertTypeAssignments" | "AlertTypeAssignments ";
}

interface GetAlertObjectsBody {
  alertAssignment: {
    id: number;
    subtypeId: number | null;
  };
  monitoringProfileAssignments: {
    id: number;
  }[];
}

interface GetAlertObjectsParams {
  clientId: number;
  payload: GetAlertObjectsBody;
}

interface CopyProfileParams {
  clientId: number;
  monitoringProfileId: number;
  name: string;
}

class MonitoringProfilesService extends API {
  async getProfiles({ clientId }: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles`,
        headers: { ...API.getAuthHeaders() },
      })
    );

    return result;
  }

  async getProfile({ clientId, profileId, view = "Basic" }: GetProfileParams) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${profileId}`,
        headers: { ...API.getAuthHeaders() },
        query: { view },
      })
    );

    return result;
  }

  async getAvailableResources({ clientId }: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/available-resources`,
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to fetch data.",
      })
    );

    return result;
  }

  async getAvailableAlertTypes({ clientId }: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/available-alert-types`,
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to fetch data.",
      })
    );

    return result;
  }

  async getNotificationChannels({ clientId }: any) {
    const result = await super.call(
      new ApiGetCallParams({
        action: `v2/clients/${clientId}/alerting/notification-channels`,
        headers: { ...API.getAuthHeaders() },
        failureMessage: "Failed to fetch notification channels.",
      })
    );

    return result;
  }

  async createProfile({
    clientId,
    name,
    timeZone,
    activityWindows,
    description,
    isSyncedToClients,
  }: CreateProfileModel) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles`,
        headers: { ...API.getAuthHeaders() },
        body: {
          name,
          description,
          timeZone,
          activityWindows,
          isSyncedToClients,
        },
        successMessage: "Profile created successfully.",
        failureMessage: "Failed to create profile.",
      })
    );

    return result;
  }

  async updateProfileDescription({
    clientId,
    name,
    timeZone,
    activityWindows,
    description,
    monitoringProfileId,
  }: UpdateProfileDescriptionModel) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}`,
        headers: { ...API.getAuthHeaders() },
        body: {
          name,
          description,
          timeZone,
          activityWindows,
        },
        successMessage: "Profile updated successfully.",
        failureMessage: "Failed to update profile.",
      })
    );

    return result;
  }

  async deleteProfile({ clientId, monitoringProfileId }: any) {
    const result = await super.call(
      new ApiDeleteCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}`,
        headers: { ...API.getAuthHeaders() },
        successMessage: "Successfully deleted profile.",
        failureMessage: "Could not delete profile id.",
      })
    );

    return result;
  }

  async assignResources({ clientId, monitoringProfileId, assignments }: AssignResourceModel) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}/resource-assignments`,
        headers: { ...API.getAuthHeaders() },
        body: {
          assignments,
        },
        successMessage: "Resources successfully assigned.",
        failureMessage: "Failed to assign resources.",
      })
    );

    return result;
  }

  async assignAlertType({ clientId, monitoringProfileId, assignments }: AssignAlertTypeModel) {
    const filteredAssignments = assignments.map((ass) => ({
      ...ass,
      objectAssignments: ass.objectAssignments.filter((obj) => obj.name !== ALL_OBJECTS_LABEL),
    }));

    const result = await super.call(
      new ApiPutCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}/alert-type-assignments`,
        headers: { ...API.getAuthHeaders() },
        body: {
          assignments: filteredAssignments,
        },
        successMessage: "Alert types successfully assigned.",
        failureMessage: "Failed to assign alert types.",
      })
    );

    return result;
  }

  async assignNotificationChannels({
    clientId,
    monitoringProfileId,
    alertOpenAssignments,
    alertCloseAssignments,
  }: AssignNotificationChannelsModel) {
    const result = await super.call(
      new ApiPutCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}/notification-channel-assignments`,
        headers: { ...API.getAuthHeaders() },
        body: {
          alertOpenAssignments,
          alertCloseAssignments,
        },
        successMessage: "Notification channels successfully assigned.",
        failureMessage: "Failed to assign notification channels.",
      })
    );

    return result;
  }

  async getAlertObjects({ clientId, payload }: GetAlertObjectsParams) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/alerting/objects:query`,
        headers: { ...API.getAuthHeaders() },
        body: payload,
        failureMessage: "Failed to fetch alert objects.",
      })
    );

    return result;
  }

  async copyProfile({ clientId, monitoringProfileId, name }: CopyProfileParams) {
    const result = await super.call(
      new ApiPostCallParams({
        action: `v2/clients/${clientId}/alerting/monitoring-profiles/${monitoringProfileId}:copy`,
        headers: { ...API.getAuthHeaders() },
        body: { name },
        successMessage: "Profile copied successfully.",
        failureMessage: "Failed to copy profile.",
      })
    );

    return result;
  }
}

export default new MonitoringProfilesService();
